import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { getAllPatientProfiles } from "../../actions/patientProfileActions";
import { getAllAppointments } from "../../actions/appointmentActions";
import { listAllInvoices } from "../../actions/invoiceActions";
import { imgUrl } from "src/actions/types";
import { formatCurrency } from "../utils/currencyHelper";
// --- Helper Functions ---
const calculateAge = (dob) => {
  if (!dob) return "N/A";
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return isNaN(age) ? "N/A" : age;
};

// const formatCurrency = (amount) => {
//   return new Intl.NumberFormat("en-IN", {
//     style: "currency",
//     currency: "INR",
//     maximumFractionDigits: 0,
//   }).format(amount || 0);
// };

// --- Main Admin Patient List Component ---
const PatientList = ({
  profiles,
  appointments,
  invoices,
  // (MODIFIED) Separate loading and error states for clarity
  isProfilesLoading,
  profilesError,
  getAllPatientProfiles,
  getAllAppointments,
  selectedCurrency,
  listAllInvoices,
}) => {
  useEffect(() => {
    getAllPatientProfiles();
    getAllAppointments();
    listAllInvoices();
  }, [getAllPatientProfiles, getAllAppointments, listAllInvoices]);

  // --- Memoized hook to enrich patient data with dynamic stats ---
  const enrichedPatients = useMemo(() => {
    // This hook will now run as long as profiles are available,
    // even if appointments or invoices are empty.
    if (!profiles || profiles.length === 0) return [];

    const appointmentMap = new Map();
    if (appointments && appointments.length > 0) {
      appointments.forEach((apt) => {
        const patientAppointments = appointmentMap.get(apt.PatientID) || [];
        appointmentMap.set(apt.PatientID, [...patientAppointments, apt]);
      });
    }

    const invoiceMap = new Map();
    if (invoices && invoices.length > 0) {
      invoices.forEach((inv) => {
        const patientInvoices = invoiceMap.get(inv.PatientID) || [];
        invoiceMap.set(inv.PatientID, [...patientInvoices, inv]);
      });
    }

    return profiles.map((patient) => {
      const patientAppointments = appointmentMap.get(patient.PatientID) || [];
      const patientInvoices = invoiceMap.get(patient.PatientID) || [];

      // Calculate Last Visit
      let lastVisit = "N/A";
      if (patientAppointments.length > 0) {
        const latestDate = new Date(
          Math.max(
            ...patientAppointments.map((apt) => new Date(apt.appointmentDate)),
          ),
        );
        lastVisit = latestDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }

      // Calculate Total Paid Amount from Invoices with "Paid" status
      const totalPaid = patientInvoices
        .filter((inv) => inv.Status && inv.Status.toLowerCase() === "paid")
        .reduce((sum, inv) => sum + (parseFloat(inv.TotalAmount) || 0), 0);

      return {
        ...patient,
        lastVisit,
        totalPaid,
      };
    });
  }, [profiles, appointments, invoices]);

  return (
    <div className="page-wrapper ml-0">
      <div className="content-container">
        <div className="page-header filter-bar rounded">
          <div className="row align-items-center">
            <div className="col">
              <h4 className="page-title"> Patients-List</h4>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/doctor/dashboard">Admin Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Patients-List</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="datatable table table-hover table-center mb-0">
                    <thead>
                      <tr>
                        <th>Patient ID</th>
                        <th>Patient Name</th>
                        <th>Age</th>
                        <th>Address</th>
                        <th>Phone</th>
                        <th>Last Visit</th>
                        <th className="text-right">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* --- (MODIFIED) More intelligent render logic --- */}
                      {isProfilesLoading ? (
                        <tr>
                          <td colSpan="7" className="text-center p-5">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : profilesError ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center text-danger p-4"
                          >
                            <strong>Error:</strong> {profilesError}
                          </td>
                        </tr>
                      ) : enrichedPatients.length > 0 ? (
                        enrichedPatients.map((patient) => {
                          const fullName = `${patient.firstName || ""} ${
                            patient.lastName || ""
                          }`;
                          const imageSrc = patient.uploadPhoto
                            ? `${imgUrl}${patient.uploadPhoto}`
                            : "/assets/img/patients/patient1.jpg";

                          return (
                            <tr key={patient.PatientID}>
                              <td>{patient.PatientID}</td>
                              <td>
                                <h2 className="table-avatar">
                                  <a className="avatar avatar-sm mr-2">
                                    <img
                                      className="avatar-img rounded-circle"
                                      src={imageSrc}
                                      alt="Patient"
                                    />
                                  </a>
                                  <h6>{fullName}</h6>
                                </h2>
                              </td>
                              <td>{calculateAge(patient.dateOfBirth)}</td>
                              <td>{`${patient.CDAddressLine1 || ""} ${
                                patient.CDCity || ""
                              }`}</td>
                              <td>{patient.phoneNumber || "N/A"}</td>
                              <td>{patient.lastVisit}</td>
                              <td className="text-right text-success font-weight-bold">
                                {formatCurrency(
                                  patient.totalPaid,
                                  selectedCurrency,
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center p-4">
                            No patients found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- (MODIFIED) Map state to props with more specific error/loading checks ---
const mapStateToProps = (state) => ({
  profiles: state.patientProfile?.profiles || [],
  appointments: state.appointment?.all || [],
  invoices: state.invoice?.invoices || [],
  // We only care about the loading state of the ESSENTIAL data for the main display
  isProfilesLoading: state.patientProfile?.loading,
  // Only show a fatal error if the ESSENTIAL data fails to load
  profilesError: state.patientProfile?.error,
  selectedCurrency: state.currencyReducer.selectedCurrency,
});

export default connect(mapStateToProps, {
  getAllPatientProfiles,
  getAllAppointments,
  listAllInvoices,
})(PatientList);
